import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchSession } from "../lib/api-client";

function UserAccountPage(): JSX.Element {
  const [passwordForm, setPasswordForm] = useState({ current: "", new: "", confirm: "" });
  const [passwordMessage, setPasswordMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
  });

  const session = sessionQuery.data?.session;

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordMessage({ type: "error", text: "Passwords do not match" });
      return;
    }

    if (passwordForm.new.length < 8) {
      setPasswordMessage({ type: "error", text: "Password must be at least 8 characters" });
      return;
    }

    try {
      // TODO: Implement password change endpoint
      setPasswordMessage({ type: "success", text: "Password changed successfully" });
      setPasswordForm({ current: "", new: "", confirm: "" });
    } catch (error) {
      setPasswordMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to change password",
      });
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0 }}>My Account</h1>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            Manage your profile and account settings.
          </p>
        </div>
      </header>

      <main style={{ padding: "2rem", display: "grid", gap: "2rem", flex: 1, maxWidth: "600px" }}>
        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1.5rem" }}>
            <h2 style={{ marginBottom: "0.3rem" }}>Profile Information</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Your account details.
            </p>
          </header>

          {sessionQuery.isLoading ? (
            <div style={{ color: "var(--color-text-muted)" }}>Loading profile...</div>
          ) : (
            <div style={{ display: "grid", gap: "1rem" }}>
              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
                  Email
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    background: "var(--color-bg-solid)",
                    borderRadius: "var(--radius-base)",
                    fontSize: "0.9rem",
                  }}
                >
                  {session?.email}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
                  Roles
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    background: "var(--color-bg-solid)",
                    borderRadius: "var(--radius-base)",
                    fontSize: "0.9rem",
                  }}
                >
                  {session?.roles.join(", ") || "User"}
                </div>
              </div>

              <div>
                <label style={{ display: "block", fontSize: "0.875rem", fontWeight: 500, marginBottom: "0.25rem" }}>
                  Teams
                </label>
                <div
                  style={{
                    padding: "0.75rem",
                    background: "var(--color-bg-solid)",
                    borderRadius: "var(--radius-base)",
                    fontSize: "0.9rem",
                  }}
                >
                  {session?.memberships.length ?? 0} team{(session?.memberships.length ?? 0) !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="surface" style={{ padding: "1.5rem" }}>
          <header style={{ marginBottom: "1rem" }}>
            <h2 style={{ marginBottom: "0.3rem" }}>Change Password</h2>
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Update your account password.
            </p>
          </header>

          <form onSubmit={handlePasswordChange} style={{ display: "grid", gap: "0.75rem" }}>
            <label className="field">
              <span>Current password</span>
              <input
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                required
              />
            </label>

            <label className="field">
              <span>New password</span>
              <input
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                required
              />
            </label>

            <label className="field">
              <span>Confirm password</span>
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                required
              />
            </label>

            {passwordMessage && (
              <div
                style={{
                  padding: "0.75rem",
                  borderRadius: "var(--radius-base)",
                  background: passwordMessage.type === "success" ? "var(--color-success-soft)" : "var(--color-critical-soft)",
                  color: passwordMessage.type === "success" ? "var(--color-success)" : "var(--color-critical)",
                  fontSize: "0.875rem",
                }}
              >
                {passwordMessage.text}
              </div>
            )}

            <div>
              <button type="submit" className="button primary">
                Update Password
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default UserAccountPage;
